import React, { useCallback, useContext, useMemo, useState } from 'react';
import Layout from '../../components/layout/main';
import { Fade, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getDataFull, deleteData } from '../../services/ajax/';
import { AuthContext } from '../../context/auth';
import { GlobalsContext } from '../../context/globals';
import MaterialTable, { DataType } from '../../components/material-table';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { blue, green, red } from '@mui/material/colors';
import { useSnackbar } from 'notistack';
import { AlertModalContext } from '../../components/modal/alert';
import { LoadingModalContext } from '../../components/modal/loading';

function Page() {
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const globalsContext = useContext(GlobalsContext);
    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);
    const { enqueueSnackbar } = useSnackbar();

    const [rowsTableMain, setRowsTableMain] = useState(null);
    let [tableMainNeedUpdate, setTableMainNeedUpdate] = useState(Date.now());

    const onNuevo = useCallback(() => {
        globalsContext.setIdEdit(false);
        navigate('/area/nuevo');
    }, [globalsContext, navigate]);

    const onEditar = useCallback((row) => {
        globalsContext.setIdEdit(row.id_area);
        navigate('/area/editar');
    }, [globalsContext, navigate]);

    const onEliminar = useCallback((row) => {
        alertModal.showModal(
            'Eliminar área',
            'El área ' + row.nombre + ' será eliminada. ¿Deseas continuar?',
            async function () {
                loadingModal.setOpen(true);
                const response = await deleteData(`areas/${row.id_area}`, usuario.token);
                if (response.error === '') {
                    enqueueSnackbar('Área eliminada', { variant: 'success' });
                    setTableMainNeedUpdate(Date.now());
                } else {
                    enqueueSnackbar(response.error, { variant: 'error' });
                }
                loadingModal.setOpen(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usuario.token]);

    const tableMain = useMemo(() => ({
        title: 'Áreas (CUOF)',
        idName: 'id_area',
        defaultOrder: 'asc',
        headers: [
            { id: 'cuof', label: 'CUOF', dataType: DataType.TEXT, sortable: true },
            { id: 'nombre', label: 'Nombre', dataType: DataType.TEXT, sortable: true },
            { id: 'tipo_area', label: 'Tipo', dataType: DataType.TEXT, sortable: false },
            { id: 'organismo', label: 'Organismo', dataType: DataType.TEXT, sortable: false },
        ],
        loadData: async (orderBy, order, page, rowsPerPage, searchText, selectedFilter, pageUpdate, totalUpdate, filterListUpdate) => {
            setRowsTableMain(null);
            const params = new URLSearchParams();
            params.append('per_page', rowsPerPage.toString());
            params.append('page', (page + 1).toString());
            if (orderBy) params.append('order_by', orderBy);
            if (order) params.append('order', order);
            if (searchText) params.append('search_text', searchText);

            const response = await getDataFull(`areas?${params.toString()}`, usuario.token);
            if (response.error === '') {
                const tablePage = response.meta?.page ? response.meta.page - 1 : page;
                pageUpdate(tablePage);
                totalUpdate(response.meta?.total || 0);
                filterListUpdate([]);
                setRowsTableMain(response.data || []);
            } else {
                enqueueSnackbar(response.error, { variant: 'error' });
            }
        },
        moreMenuItems: [
            {
                label: 'Crear nueva área',
                secondary: 'Agregar un área con su CUOF',
                icon: <AddCircleOutlineRoundedIcon sx={{ color: green[600] }} />,
                itemColor: green[600],
                action: () => onNuevo(),
            },
        ],
        rowActions: (row) => ([
            {
                label: 'Modificar área',
                secondary: 'Cambiar nombre, CUOF u organismo',
                icon: <EditRoundedIcon sx={{ color: blue[600] }} />,
                itemColor: blue[600],
                action: () => onEditar(row),
            },
            {
                label: 'Eliminar área',
                secondary: 'Se marca como eliminada.',
                icon: <DeleteRoundedIcon sx={{ color: red[600] }} />,
                itemColor: red[600],
                dividerBefore: true,
                action: () => onEliminar(row),
            },
        ]),
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [usuario.token]);

    return (
        <Layout nombrePagina="Áreas (CUOF)">
            <Fade in={true} timeout={900}>
                <Grid size={12}>
                    <MaterialTable
                        title={tableMain.title}
                        loadData={tableMain.loadData}
                        headers={tableMain.headers}
                        data={rowsTableMain}
                        moreMenuItems={tableMain.moreMenuItems}
                        selectable={false}
                        rowActions={tableMain.rowActions}
                        idName={tableMain.idName}
                        defaultOrder={tableMain.defaultOrder}
                        defaultRowsPerPage={5}
                        needUpdate={tableMainNeedUpdate}
                    />
                </Grid>
            </Fade>
        </Layout>
    );
}

export default Page;
